/**
 * useRealtimeSubscription Hook
 * Canon: SUPABANK CANON v1.0.8
 * 
 * Subscribe to Supabase Realtime updates
 * Auto-invalidates React Query cache on changes
 */

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const isPrd = import.meta.env.VITE_ENV_LABEL === 'PRD';

type PostgresChange = RealtimePostgresChangesPayload<Record<string, unknown>>;

interface UseRealtimeSubscriptionOptions {
  /** Table to subscribe to */
  table: string;

  /** Schema name (default: 'public') */
  schema?: string;

  /** Event types to listen for */
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';

  /** React Query keys to invalidate on change */
  invalidateKeys?: string[][];

  /** Custom callback for changes */
  onInsert?: (payload: PostgresChange) => void;
  onUpdate?: (payload: PostgresChange) => void;
  onDelete?: (payload: PostgresChange) => void;

  /** Filter condition (e.g., 'status=eq.active') */
  filter?: string;

  /** Enable/disable subscription */
  enabled?: boolean;
}

export function useRealtimeSubscription({
  table,
  schema = 'public',
  event = '*',
  invalidateKeys = [],
  onInsert,
  onUpdate,
  onDelete,
  filter,
  enabled = true,
}: UseRealtimeSubscriptionOptions) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  
  // Memoize invalidateKeys to prevent subscription recreation
  const invalidateKeysRef = useRef(invalidateKeys);
  invalidateKeysRef.current = invalidateKeys;

  // Stable refs for callbacks to prevent subscription recreation
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);

  // Update refs when callbacks change (without triggering re-subscription)
  useEffect(() => {
    onInsertRef.current = onInsert;
    onUpdateRef.current = onUpdate;
    onDeleteRef.current = onDelete;
  }, [onInsert, onUpdate, onDelete]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Create unique channel name
    const channelName = `${table}_changes_${Date.now()}`;

    // Subscribe to changes
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event,
          schema,
          table,
          filter,
        },
          (payload: PostgresChange) => {
            if (!isPrd) console.log(`[Realtime] ${payload.eventType} on ${table}:`, payload);

          // Call custom callbacks using refs (stable)
          switch (payload.eventType) {
            case 'INSERT':
              onInsertRef.current?.(payload);
              break;
            case 'UPDATE':
              onUpdateRef.current?.(payload);
              break;
            case 'DELETE':
              onDeleteRef.current?.(payload);
              break;
          }

          // Invalidate React Query cache (using ref for stable reference)
          invalidateKeysRef.current.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        }
      )
      .subscribe((status) => {
        if (!isPrd) console.log(`[Realtime] ${table} subscription status:`, status);
      });

    channelRef.current = channel;

    // Cleanup on unmount
    return () => {
      if (!isPrd) console.log(`[Realtime] Unsubscribing from ${table}`);
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [table, schema, event, filter, enabled]); // ✅ Removed queryClient & invalidateKeys (using refs)

  return {
    channel: channelRef.current,
    isSubscribed: channelRef.current !== null,
  };
}

/**
 * Subscribe to multiple tables at once
 * Note: Manages all subscriptions in a single hook to comply with rules of hooks.
 * The subscription config is used as a dependency - changes will recreate all subscriptions.
 */
export function useRealtimeSubscriptions(
  subscriptions: Omit<UseRealtimeSubscriptionOptions, 'enabled'>[]
) {
  const queryClient = useQueryClient();
  const channelsRef = useRef<RealtimeChannel[]>([]);
  const subscriptionsRef = useRef(subscriptions);
  subscriptionsRef.current = subscriptions;

  useEffect(() => {
    // Unsubscribe from previous channels
    channelsRef.current.forEach(channel => channel.unsubscribe());
    channelsRef.current = [];

    // Subscribe to all tables
    const channels = subscriptionsRef.current.map((config) => {
      const { table, schema = 'public', event = '*', invalidateKeys = [], onInsert, onUpdate, onDelete, filter } = config;
      const channelName = `${table}_changes_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event, schema, table, filter },
          (payload: PostgresChange) => {
            if (!isPrd) console.log(`[Realtime] ${payload.eventType} on ${table}:`, payload);

            switch (payload.eventType) {
              case 'INSERT':
                onInsert?.(payload);
                break;
              case 'UPDATE':
                onUpdate?.(payload);
                break;
              case 'DELETE':
                onDelete?.(payload);
                break;
            }

            invalidateKeys.forEach((key) => {
              queryClient.invalidateQueries({ queryKey: key });
            });
          }
        )
        .subscribe((status) => {
          if (!isPrd) console.log(`[Realtime] ${table} subscription status:`, status);
        });

      return channel;
    });

    channelsRef.current = channels;

    return () => {
      channels.forEach(channel => channel.unsubscribe());
      channelsRef.current = [];
    };
  }, [subscriptions.length, queryClient]); // Recreate when subscription count changes

  return {
    channels: channelsRef.current,
    allSubscribed: channelsRef.current.length === subscriptions.length && channelsRef.current.length > 0,
  };
}